import * as z from "zod";
import { TFunction } from "i18next";
import { SAUDI_UNIFIED_NUMBER_PATTERN } from "../../../setup/util/partyCountry.helpers";

export type TBnplProvider = "tabby" | "tamara" | "neo";

export type TwoPartyContractInputs = {
  title: string;
  description: string;
  /** Uploaded S3 urls; PDFs go to the milestone's attachments, the rest to its images. */
  files: string[];
  buyer: {
    isCompany: boolean;
    name: string;
    /** Dial code without "+", e.g. "966". */
    phoneCountryCode: string;
    phoneNational: string;
    companyName: string;
    companyUnifiedNumber: string;
  };
  amount: number | null;
  installments: Record<TBnplProvider, boolean>;
};

/** Form fields validated before leaving each step. */
export const STEP_FIELDS: (keyof TwoPartyContractInputs | string)[][] = [
  ["title", "description"],
  [
    "buyer.name",
    "buyer.phoneNational",
    "buyer.companyName",
    "buyer.companyUnifiedNumber",
  ],
  ["amount"],
];

export const DEFAULT_VALUES: TwoPartyContractInputs = {
  title: "",
  description: "",
  files: [],
  buyer: {
    isCompany: false,
    name: "",
    phoneCountryCode: "966",
    phoneNational: "",
    companyName: "",
    companyUnifiedNumber: "",
  },
  amount: null,
  installments: { tabby: false, tamara: false, neo: false },
};

interface ISchemaOptions {
  t: TFunction;
  isPhoneValid: (countryCode: string, national: string) => boolean;
  maxAmount: number;
  /** The seller's own mobiles, E.164; the buyer may not use one of them (WAF-3235). */
  sellerPhones?: string[];
  /** The buyer's national digits as E.164, to compare with `sellerPhones`. */
  toE164?: (national: string) => string;
}

export const buildSchema = ({
  t,
  isPhoneValid,
  maxAmount,
  sellerPhones = [],
  toE164,
}: ISchemaOptions) =>
  z
    .object({
      title: z
        .string()
        .trim()
        .min(5, t("err_min_chars", { min: 5 }))
        .max(100, t("err_max_chars", { max: 100 })),
      description: z.string().max(2000, t("err_max_chars", { max: 2000 })),
      files: z.array(z.string()),
      buyer: z.object({
        isCompany: z.boolean(),
        name: z.string(),
        phoneCountryCode: z.string(),
        phoneNational: z.string(),
        companyName: z.string(),
        companyUnifiedNumber: z.string(),
      }),
      amount: z
        .number({ message: t("err_required") })
        .nullable()
        .refine((value) => value !== null && value > 0, {
          message: t("err_required"),
        })
        .refine((value) => value === null || value <= maxAmount, {
          message: t("err_max_amount", { max: maxAmount }),
        }),
      installments: z.object({
        tabby: z.boolean(),
        tamara: z.boolean(),
        neo: z.boolean(),
      }),
    })
    .superRefine(({ buyer }, ctx) => {
      const name = buyer.name.trim();
      if (name.length < 5 || name.length > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["buyer", "name"],
          message:
            name.length < 5
              ? t("err_min_chars", { min: 5 })
              : t("err_max_chars", { max: 100 }),
        });
      }
      if (!isPhoneValid(buyer.phoneCountryCode, buyer.phoneNational)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["buyer", "phoneNational"],
          message: t("err_invalid_phone"),
        });
      } else if (toE164 && sellerPhones.includes(toE164(buyer.phoneNational))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["buyer", "phoneNational"],
          message: t("err_buyer_is_seller_phone"),
        });
      }
      if (!buyer.isCompany) return;
      const companyName = buyer.companyName.trim();
      if (companyName.length < 5 || companyName.length > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["buyer", "companyName"],
          message:
            companyName.length < 5
              ? t("err_min_chars", { min: 5 })
              : t("err_max_chars", { max: 100 }),
        });
      }
      if (!SAUDI_UNIFIED_NUMBER_PATTERN.test(buyer.companyUnifiedNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["buyer", "companyUnifiedNumber"],
          message: t("err_invalid_unified_number"),
        });
      }
    });
